import { FarmApiArgs } from 'common/models/crypto';
import { AppSyncFarm } from 'common/models/farm';
import { IchiInsights } from 'common/models/Insights';
import { Nullable } from 'common/models/optional';
import { CryptoUtils, GAS_SETTINGS } from 'utils/cryptoUtils';
import { FarmUtils } from 'utils/farmUtils';
import { NumberUtils } from 'utils/numberUtils';
import { StorageUtils } from 'utils/storageUtils';
import { VaultDepositEvent } from 'xstate/vaultDepositEvent';
import { FarmWithdrawEvent } from 'xstate/farmWithdrawEvent';
import { VaultDepositContext } from 'xstate/vaultDepositMachine';
import numbro from 'numbro';
import { isUserRejectedError, isViemBaseError } from 'utils/errorUtils';
import {
  ChainId,
  ETH_VAULT_WRAPPER,
  MKR_TOKENS,
  getDepositGuardAddress,
} from 'constants/constants';
import { readContract, writeContract, simulateContract } from '@wagmi/core';
import { erc20 } from 'constants/abis/erc20';
import { Address, BaseError, UserRejectedRequestError } from 'viem';
import { multicall } from 'constants/abis/multicall';
import { ichiVault } from 'constants/abis/ichiVault';
import { getPublicClient } from '@wagmi/core';
import { depositGuard } from 'constants/abis/depositGuard';
import { ethVaultWithSlippage } from 'constants/abis/ethVaultWithSlippage';
import { usdtOnEthereum } from 'constants/abis/usdtOnEthereum';
import { wagmiConf } from 'containers/WalletProvider/WalletProvider';

export namespace VaultApi {
  export type GetVaultDataResponse = {
    isTokenApproved: boolean;
    tokenMaxDeposit: bigint;
    tokenAllowance: bigint;
    tokenBalance: bigint;
    tokenDecimals: number;
    lpBalance: bigint;
    message?: string;
  };

  type DepositArgs = {
    amount: string;
    amountUsd: number;
    farm: AppSyncFarm;
    token?: string;
  } & FarmApiArgs;

  type WithdrawArgs = {
    amount: string;
    amountUsd: number;
    farm: AppSyncFarm;
  } & FarmApiArgs;

  type AllowanceArgs = {
    amount: string;
    approveExactAmount: boolean;
    accountAddress: string;
    chainId: number;
    farm: AppSyncFarm;
  };

  type GetVaultDataArgs = {
    // availableToken: bigint;
    amount: string;
    accountAddress: Address;
    chainId: number;
    farm: AppSyncFarm;
    // token is only used for ETH/wETH vault because people can deposit either token
    token?: string;
  };

  export interface IVault {
    deposit(args: DepositArgs): Promise<Nullable<StorageUtils.StoredTx>>;
    withdraw(args: WithdrawArgs): Promise<Nullable<StorageUtils.StoredTx>>;
    getVaultData(args: GetVaultDataArgs): Promise<GetVaultDataResponse>;
    updateVaultTokenData(args: VaultDepositContext): GetVaultDataResponse;
    approveTokenAllowance(
      args: AllowanceArgs
    ): Promise<Nullable<StorageUtils.StoredTx>>;
    resetTokenAllowance(
      args: AllowanceArgs
    ): Promise<Nullable<StorageUtils.StoredTx>>;
  }

  export class Vault implements IVault {
    private static instance: IVault;

    static maxApproval = 2n ** 256n - 1n;
    static maxApprovalString = Vault.maxApproval.toString();
    // These will primarily be used for local storage lookups for determining if there
    // are in-flight transactions
    static depositFN = 'vaultDeposit';
    static depositFNLabel = 'Vault Deposit';
    static withdrawFN = 'vaultWithdraw';
    static withdrawFNLabel = 'Vault Withdraw';
    static approveTokenAllowanceFN = 'approveTokenAllowance';
    static approveTokenAllowanceFNLabel = 'Approve Token';
    static resetTokenAllowanceFN = 'resetTokenAllowance';
    static resetTokenAllowanceFNLabel = 'reset Token';

    zeroApproval = 0n;

    private constructor() {}

    public static getInstance(): IVault {
      if (!Vault.instance) {
        Vault.instance = new Vault();
      }
      return Vault.instance;
    }

    async deposit(args: DepositArgs): Promise<Nullable<StorageUtils.StoredTx>> {
      let storedTx: Nullable<StorageUtils.StoredTx> = null;

      const functionName = VaultApi.Vault.depositFN;
      const currencyPrefix = !FarmUtils.isHodlVault(args.farm) ? '$' : '';
      const amount = Number(args.amount);
      // TODO: Check the logic here, I'm saying if hodl then use amount which is not the usd conversion, otherwise use the usd converstion, but this this well
      const prettyAmount = numbro(amount).format({
        thousandSeparated: true,
        mantissa: amount > 0 && amount < 0.01 ? 4 : 2,
      });
      if (args.farm.isPosition) {
        try {
          const token = FarmUtils.getBaseToken(args.farm);

          // TODO: We need to create rules around what and when to show the ${token.displayName} as a suffix
          const labels: StorageUtils.LabelsArg = {
            labels: {
              // This is showing LP not actual usd value
              executing: `Depositing ${currencyPrefix}${prettyAmount}...`,
              success: `Successfully deposited ${currencyPrefix}${prettyAmount}!`,
              error: `Error depositing ${currencyPrefix}${prettyAmount}!`,
            },
          };

          // Otherwise there is no tx in flight, execute contract call
          const stringBN = NumberUtils.stringToBN(
            args.amount,
            BigInt(token.decimals)
          );
          // console.debug(`depositing: ${args.amount}, stringBN: ${stringBN} from account: ${args.accountAddress}`);
          const adjPoolId = FarmUtils.getAdjustedPoolId(
            BigInt(args.farm.poolId),
            args.chainId
          );

          let tx: `0x${string}`;

          // this assignment is for inverted vaults: when token1 is deposited. wETH vault is inverted
          let token0toDeposit = 0n;
          let token1toDeposit = stringBN;

          // depositing ETH? - only applicable to the wETH vault
          const isTokenEth =
            args.token === 'eth' &&
            token.displayName.toLowerCase() === 'weth' &&
            args.chainId !== ChainId.Polygon &&
            args.chainId !== ChainId.Celo;

          const publicClient = getPublicClient(wagmiConf);

          if (!isTokenEth) {
            // use provider for view call

            const token0vault = await readContract(wagmiConf, {
              address: args.farm.lpAddress,
              abi: ichiVault,
              functionName: 'token0',
              chainId: args.chainId as ChainId,
            });

            console.log({args}, {token}, {stringBN}, {depositGuard}, getDepositGuardAddress(args.farm.lpAddress, args.chainId));

            // use signer for callStatic and real TX
            let { result: lpAmount } = await simulateContract(wagmiConf, {
              abi: depositGuard,
              address: getDepositGuardAddress(args.farm.lpAddress, args.chainId),
              functionName: 'forwardDepositToICHIVault',
              args: [
                args.farm.lpAddress,
                args.farm.lpDeployer,
                token.address,
                stringBN,
                0n,
                args.accountAddress,
              ],
              account: args.accountAddress,
            });

            // remove 1% of estimated lp amount
            lpAmount = (lpAmount * 99n) / 100n;

            if (token.address.toLowerCase() === token0vault.toLowerCase()) {
              token0toDeposit = stringBN;
              token1toDeposit = 0n;
            }

            tx = await writeContract(wagmiConf, {
              address: getDepositGuardAddress(args.farm.lpAddress, args.chainId),
              abi: depositGuard,
              functionName: 'forwardDepositToICHIVault',
              args: [
                args.farm.lpAddress,
                args.farm.lpDeployer,
                token.address,
                stringBN,
                lpAmount,
                args.accountAddress,
              ],
              gas: CryptoUtils.calculateGasMargin(
                BigInt(GAS_SETTINGS.vaultDepositWithGuard),
                args.chainId
              ),
            });
          } else {
            // to be used for the actual transaction
            let { result: lpAmount } = await simulateContract(wagmiConf, {
              abi: ethVaultWithSlippage,
              address: ETH_VAULT_WRAPPER[args.farm.lpAddress.toLocaleLowerCase()].wrapper,
              functionName: 'depositETH',
              args: [0n, args.accountAddress],
              value: stringBN,
              account: args.accountAddress,
            });

            // remove 1% of estimated lp amount
            lpAmount = (lpAmount * 99n) / 100n;

            // actual transaction
            tx = await writeContract(wagmiConf, {
              abi: ethVaultWithSlippage,
              address: ETH_VAULT_WRAPPER[args.farm.lpAddress.toLocaleLowerCase()].wrapper,
              functionName: 'depositETH',
              args: [lpAmount, args.accountAddress],
              value: stringBN,
              gas: CryptoUtils.calculateGasMargin(
                BigInt(GAS_SETTINGS.ethVaultDepositWithGuard),
                args.chainId
              ),
            });
          }

          storedTx = await StorageUtils.storeTx({
            ...args,
            hash: tx,
            functionName,
            labels: labels.labels,
            value: args.amount,
          });

          IchiInsights.trackEvent({
            event: VaultDepositEvent.DEPOSIT,
            properties: {
              ...args,
              token0amountBNString: token0toDeposit,
              token1amountBNString: token1toDeposit,
              adjPoolId: adjPoolId,
              handledAt: `vaultApi:${Vault.depositFN}`,
              storedTx,
            },
          });
        } catch (err) {
          console.error(err);
          if (isUserRejectedError(err as BaseError)) {
            throw new Error((err as UserRejectedRequestError).shortMessage);
          }
          const error = isViemBaseError(err as BaseError)
            ? new Error((err as BaseError).shortMessage)
            : err;

          IchiInsights.trackException({
            error: error as Error,
            properties: {
              ...args,
              handledAt: `vaultApi:${Vault.depositFN}`,
            },
          });
          throw error;
        }
      } else {
        const message = 'Could not deposit: no contract specifiedvaultApi';
        const error = new Error(message);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            handledAt: `vaultApi:${Vault.depositFN}`,
          },
        });
        throw error;
      }
      return storedTx;
    }

    async withdraw(
      args: WithdrawArgs
    ): Promise<Nullable<StorageUtils.StoredTx>> {
      let storedTx: Nullable<StorageUtils.StoredTx> = null;

      const functionName = VaultApi.Vault.withdrawFN;

      if (args.farm.isPosition) {
        try {
          const stringBN = NumberUtils.stringToBN(args.amount, 18n);

          const adjPoolId = FarmUtils.getAdjustedPoolId(
            BigInt(args.farm.poolId),
            args.chainId
          );

          let amountUsd = args.amountUsd;
          const isHodlVault = FarmUtils.isHodlVault(args.farm);
          if (isHodlVault && args.farm) {
            const baseToken = FarmUtils.getBaseToken(args.farm);
            amountUsd =
              FarmUtils.amountInTokens(amountUsd, baseToken) ?? args.amountUsd;
          }

          const prettyAmount = numbro(Number(amountUsd)).format({
            thousandSeparated: true,
            mantissa: amountUsd > 0 && amountUsd < 0.01 ? 4 : 2,
          });
          const currencyPrefix = !isHodlVault ? '$' : '';

          // TODO: We need to create rules around what and when to show the ${token.displayName} as a suffix
          const labels: StorageUtils.LabelsArg = {
            labels: {
              executing: `Withdrawing ${currencyPrefix}${prettyAmount}...`,
              success: `Successfully withdrew ${currencyPrefix}${prettyAmount}!`,
              error: `Error withdrawing ${currencyPrefix}${prettyAmount}!`,
            },
          };
          const tx = await writeContract(wagmiConf, {
            address: args.farm.lpAddress,
            abi: ichiVault,
            functionName: 'withdraw',
            args: [stringBN, args.accountAddress],
            gas: CryptoUtils.calculateGasMargin(
              BigInt(GAS_SETTINGS.vaultWithdraw),
              args.chainId
            ),
          });

          storedTx = await StorageUtils.storeTx({
            ...args,
            hash: tx,
            functionName,
            labels: labels.labels,
            value: args.amount,
          });

          IchiInsights.trackEvent({
            event: FarmWithdrawEvent.WITHDRAW,
            properties: {
              ...args,
              amountBNString: stringBN,
              vault: args.farm.lpAddress,
              poolId: `${args.farm.poolId}`,
              adjPoolId: `${adjPoolId}`,
              handledAt: `vaultApi:${Vault.withdrawFN}`,
              storedTx,
            },
          });
        } catch (err) {
          console.error(err);
          if (isUserRejectedError(err as BaseError)) {
            throw new Error((err as UserRejectedRequestError).shortMessage);
          }

          const message = `Unable to withdraw ${args.amount}`;
          const error = new Error(message);
          IchiInsights.trackException({
            error,
            properties: {
              ...args,
              handledAt: `vaultApi:${Vault.withdrawFN}`,
            },
          });
          throw error;
        }
      } else {
        const message = 'Could not withdraw, no contract specified';
        const error = new Error(message);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            handledAt: `vaultApi:${Vault.withdrawFN}`,
          },
        });
        throw error;
      }
      return storedTx;
    }

    async getVaultData(args: GetVaultDataArgs): Promise<GetVaultDataResponse> {
      let isTokenApproved = false;
      let tokenAllowance = 0n;
      let tokenMaxDeposit = BigInt(CryptoUtils.MAX_NUMBER);
      let tokenBalance = 0n;
      let lpBalance = 0n;
      let tokenDecimals = 18;
      let message = '';
      try {
        const chainId = args.farm.chainId;

        const token = FarmUtils.getBaseToken(args.farm);
        tokenDecimals = token.decimals;

        // Check Allowance
        tokenAllowance = args.accountAddress
          ? await readContract(wagmiConf, {
              address: token.address,
              abi: erc20,
              functionName: 'allowance',
              args: [args.accountAddress, getDepositGuardAddress(args.farm.lpAddress, args.chainId)],
              chainId: args.chainId as ChainId,
            })
          : 0n;

        // Get Token Balance
        tokenBalance = args.accountAddress
          ? await readContract(wagmiConf, {
              address: token.address,
              abi: erc20,
              functionName: 'balanceOf',
              args: [args.accountAddress],
              chainId: args.chainId as ChainId,
            })
          : 0n;

        const tokenAmount = NumberUtils.stringToBN(
          args.amount,
          BigInt(token.decimals)
        );
        // isTokenApproved = tokenAllowance.gt(0) && tokenAllowance.gte(tokenBalance);
        isTokenApproved = tokenAllowance > 0n && tokenAllowance >= tokenAmount;

        // for wETH vault: token could be ETH or wETH
        const isTokenEth =
          args.token === 'eth' &&
          token.displayName.toLowerCase() === 'weth' &&
          chainId !== ChainId.Polygon &&
          chainId !== ChainId.Celo;
        if (isTokenEth) {
          isTokenApproved = true;
          tokenAllowance = tokenAmount;
          const ethTokenAddress = (MKR_TOKENS[chainId] || {}).address;

          const ethTokenBalance = args.accountAddress
            ? await readContract(wagmiConf, {
                address: ethTokenAddress as Address,
                abi: multicall,
                functionName: 'getEthBalance',
                args: [args.accountAddress],
                chainId: args.chainId as ChainId,
              })
            : 0n;

          tokenBalance = ethTokenBalance;
        }

        // Get LP balance
        lpBalance = args.accountAddress
          ? await readContract(wagmiConf, {
              address: args.farm.lpAddress,
              abi: ichiVault,
              functionName: 'balanceOf',
              args: [args.accountAddress],
              chainId: args.chainId as ChainId,
            })
          : 0n;

        const token0vault = await readContract(wagmiConf, {
          address: args.farm.lpAddress,
          abi: ichiVault,
          functionName: 'token0',
          chainId: args.chainId as ChainId,
        });

        tokenMaxDeposit = await readContract(wagmiConf, {
          address: args.farm.lpAddress,
          abi: ichiVault,
          chainId: args.chainId as ChainId,
          functionName:
            token.address.toLowerCase() === token0vault?.toLowerCase()
              ? 'deposit0Max'
              : 'deposit1Max',
        });
      } catch (error) {
        IchiInsights.trackException({
          error: error as Error,
          properties: {
            ...args,
            handledAt: 'vaultApi:getVaultData',
          },
        });
        console.warn('Error getting vault data', error);
        message = "Couldn't get vault data.";
        throw new Error("Couldn't get vault data!");
      }
      const response: GetVaultDataResponse = {
        isTokenApproved,
        tokenAllowance,
        tokenMaxDeposit,
        tokenBalance,
        tokenDecimals,
        lpBalance,
        message,
      };

      return response;
    }

    updateVaultTokenData(args: VaultDepositContext): GetVaultDataResponse {
      if (
        !args.amount ||
        !args.dataResponse ||
        args.amount === '' ||
        !args.farm
      ) {
        throw new Error('No user data available!');
      }

      const response = args.dataResponse;
      const amountBN = NumberUtils.stringToBNString(
        args.amount,
        BigInt(FarmUtils.getBaseTokenDecimals(args.farm))
      );
      response.tokenBalance = response.tokenBalance - BigInt(amountBN);

      console.debug('getVaultTokenData', response);
      return response;
    }

    async approveTokenAllowance(
      args: AllowanceArgs
    ): Promise<Nullable<StorageUtils.StoredTx>> {
      let storedTx: Nullable<StorageUtils.StoredTx> = null;

      if (!args.farm.lpAddress) {
        throw new Error('No Vault defined!');
      }

      const functionName = Vault.approveTokenAllowanceFN;

      try {
        const token = FarmUtils.getBaseToken(args.farm);

        const amountToApprove = args.approveExactAmount
          ? NumberUtils.stringToBNString(args.amount, BigInt(token.decimals))
          : Vault.maxApprovalString;
        const amountToApproveBN = args.approveExactAmount
          ? BigInt(
              NumberUtils.stringToBNString(args.amount, BigInt(token.decimals))
            )
          : Vault.maxApproval;

        // TODO: Check token.name here
        const labels: StorageUtils.LabelsArg = {
          labels: {
            executing: 'Approving...',
            success: 'Successfully Approved!',
            error: 'Error Approving!',
          },
        };
        // If no prior tx, execute contract function
        // Note in V1 this has lpSigner, so let's see if this just works, if not create signer
        // const tx = await tokenContract.approve(args.farm.lpAddress, amountToApproveBN, {
        //   gasLimit: CryptoUtils.calculateGasMargin(BigInt(GAS_SETTINGS.approve))
        // });

        let tx: `0x${string}`;
        // use different abi for USDT on Ethereum
        const isTokenUsdtOnEthereum = 
          token.address.toLowerCase() === '0xdAC17F958D2ee523a2206206994597C13D831ec7'.toLowerCase()
          && args.chainId === ChainId.Mainnet;
        if (isTokenUsdtOnEthereum){
          tx = await writeContract(wagmiConf, {
            address: token.address,
            abi: usdtOnEthereum,
            functionName: 'approve',
            args: [
              getDepositGuardAddress(args.farm.lpAddress, args.chainId),
              amountToApproveBN,
            ],
            gas: CryptoUtils.calculateGasMargin(
              BigInt(GAS_SETTINGS.approve),
              args.chainId
            ),
          });
        } else {
          tx = await writeContract(wagmiConf, {
            address: token.address,
            abi: erc20,
            functionName: 'approve',
            args: [
              getDepositGuardAddress(args.farm.lpAddress, args.chainId),
              amountToApproveBN,
            ],
            gas: CryptoUtils.calculateGasMargin(
              BigInt(GAS_SETTINGS.approve),
              args.chainId
            ),
          });
        }

        storedTx = await StorageUtils.storeTx({
          ...args,
          hash: tx,
          functionName,
          labels: labels.labels,
          value: args.amount,
        });

        IchiInsights.trackEvent({
          event: VaultDepositEvent.APPROVE_TOKEN_ALLOWANCE,
          properties: {
            ...args,
            amount: amountToApprove,
            handledAt: `vaultApi:${Vault.approveTokenAllowanceFN}`,
            storedTx,
          },
        });
      } catch (err) {
        console.error(err);
        if (isUserRejectedError(err as BaseError)) {
          throw new Error((err as UserRejectedRequestError).shortMessage);
        }
        const error = isViemBaseError(err as BaseError)
          ? new Error((err as BaseError).shortMessage)
          : err;

        IchiInsights.trackException({
          error: error as Error,
          properties: {
            ...args,
            handledAt: `vaultApi:${Vault.withdrawFN}`,
          },
        });
        throw error;
      }
      return storedTx;
    }

    async resetTokenAllowance(
      args: AllowanceArgs
    ): Promise<Nullable<StorageUtils.StoredTx>> {
      let storedTx: Nullable<StorageUtils.StoredTx> = null;

      if (!args.farm.lpAddress) {
        throw new Error('No Vault defined!');
      }

      const functionName = Vault.resetTokenAllowanceFN;

      try {
        const token = FarmUtils.getBaseToken(args.farm);

        // TODO: Check ${token.name} here
        const labels: StorageUtils.LabelsArg = {
          labels: {
            executing: 'Resetting Allowance...',
            success: 'Successfully Reset Allowance!',
            error: 'Error Resetting Allowance!',
          },
        };

        // If no prior tx, execute contract function
        // Note in V1 this has lpSigner, so let's see if this just works, if not create signer
        // const tx: ContractTransaction = await tokenContract.approve(args.farm.lpAddress, this.zeroApproval, {
        //   gasLimit: CryptoUtils.calculateGasMargin(BigNumber.from(GAS_SETTINGS.approve))
        // });

        const tx = await writeContract(wagmiConf, {
          address: token.address,
          abi: erc20,
          functionName: 'approve',
          args: [
            getDepositGuardAddress(args.farm.lpAddress, args.chainId),
            this.zeroApproval,
          ],
          gas: CryptoUtils.calculateGasMargin(
            BigInt(GAS_SETTINGS.approve),
            args.chainId
          ),
        });

        storedTx = await StorageUtils.storeTx({
          ...args,
          hash: tx,
          functionName,
          labels: labels.labels,
          value: args.amount,
        });

        IchiInsights.trackEvent({
          event: VaultDepositEvent.APPROVE_TOKEN_ALLOWANCE,
          properties: {
            ...args,
            amount: Vault.maxApprovalString,
            handledAt: `vaultApi:${Vault.approveTokenAllowanceFN}`,
            storedTx,
          },
        });
      } catch (err) {
        console.error(err);
        if (isUserRejectedError(err as BaseError)) {
          throw new Error((err as UserRejectedRequestError).shortMessage);
        }
        const error = isViemBaseError(err as BaseError)
          ? new Error((err as BaseError).shortMessage)
          : err;

        IchiInsights.trackException({
          error: error as Error,
          properties: {
            ...args,
            handledAt: `vaultApi:${Vault.withdrawFN}`,
          },
        });
        throw error;
      }
      return storedTx;
    }
  }
}
